import { isForexAssetType } from "./utils";

const SCALE_ALIASES = {
  "1min": "1m",
  "2min": "2m",
  "3min": "3m",
  "5min": "5m",
  "1day": "1d",
};

const DEFAULT_CHART_TIME_ZONE = "America/Los_Angeles";

const SCALE_TO_MINUTES = {
  "1m": 1,
  "2m": 2,
  "3m": 3,
  "5m": 5,
  "10m": 10,
  "15m": 15,
  "30m": 30,
  "1h": 60,
  "2h": 120,
  "3h": 180,
  "4h": 240,
};

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DETECTED_BROWSER_TIME_ZONE = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      DEFAULT_CHART_TIME_ZONE
    );
  } catch (_error) {
    return DEFAULT_CHART_TIME_ZONE;
  }
})();

const zonedPartsFormatterCache = new Map();

const resolveChartTimeZone = (timeZone) => {
  const candidate = String(timeZone || "").trim();
  const fallback = DETECTED_BROWSER_TIME_ZONE || DEFAULT_CHART_TIME_ZONE;

  if (!candidate) return fallback;

  try {
    return (
      new Intl.DateTimeFormat("en-US", { timeZone: candidate }).resolvedOptions()
        .timeZone || candidate
    );
  } catch (_error) {
    return fallback;
  }
};

const getZonedPartsFormatter = (timeZone) => {
  const resolvedTimeZone = resolveChartTimeZone(timeZone);
  if (!zonedPartsFormatterCache.has(resolvedTimeZone)) {
    zonedPartsFormatterCache.set(
      resolvedTimeZone,
      new Intl.DateTimeFormat("en-US", {
        timeZone: resolvedTimeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      })
    );
  }

  return zonedPartsFormatterCache.get(resolvedTimeZone);
};

export const normalizeChartScale = (scale) =>
  SCALE_ALIASES[String(scale || "").trim()] || String(scale || "").trim() || "1d";

export const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const parseNaiveDateString = (value) => {
  const match = String(value || "").trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );

  if (!match) return null;

  const [, year, month, day, hour = "0", minute = "0", second = "0"] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
  };
};

const parseAbsoluteDate = (raw) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const naiveParts = parseNaiveDateString(value);
  if (naiveParts) {
    return new Date(
      Date.UTC(
        naiveParts.year,
        naiveParts.month - 1,
        naiveParts.day,
        naiveParts.hour,
        naiveParts.minute,
        naiveParts.second,
        0
      )
    );
  }

  const absolute = new Date(value);
  return Number.isNaN(absolute.getTime()) ? null : absolute;
};

export const parseChartDate = (raw, timeZone = null) => {
  if (!raw) return null;

  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : new Date(raw.getTime());
  }

  if (typeof raw === "number") {
    const date = new Date(raw < 1e12 ? raw * 1000 : raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const value = String(raw).trim();
  if (!value) return null;

  const naiveParts = parseNaiveDateString(value);
  if (naiveParts) {
    return new Date(
      Date.UTC(
        naiveParts.year,
        naiveParts.month - 1,
        naiveParts.day,
        naiveParts.hour,
        naiveParts.minute,
        naiveParts.second,
        0
      )
    );
  }

  const absolute = new Date(value);
  if (Number.isNaN(absolute.getTime())) return null;

  const parts = getZonedPartsFormatter(timeZone).formatToParts(absolute);
  const get = (type) => parts.find((part) => part.type === type)?.value || null;
  const year = Number(get("year"));
  const month = Number(get("month"));
  const day = Number(get("day"));
  const hour = Number(get("hour"));
  const minute = Number(get("minute"));
  const second = Number(get("second"));

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second, 0));
};

export const toChartTimestamp = (raw, timeZone = null) => {
  const date = parseChartDate(raw, timeZone);
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
};

export const toAbsoluteTimestamp = (raw) => {
  const date = parseAbsoluteDate(raw);
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
};

const fromChartTime = (time) => {
  if (time == null) return null;

  if (typeof time === "number") {
    return new Date(time * 1000);
  }

  if (typeof time === "object") {
    const year = Number(time.year);
    const month = Number(time.month);
    const day = Number(time.day);
    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day)
    ) {
      return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    }
  }

  return parseChartDate(time);
};

const pad2 = (value) => String(value).padStart(2, "0");

const formatUtcDate = (date, withTime) => {
  if (!date) return "";
  const day = date.getUTCDate();
  const month = MONTHS_SHORT[date.getUTCMonth()];
  const year = date.getUTCFullYear().toString().slice(2);
  if (!withTime) return `${month} ${day}, '${year}`;
  return `${month} ${day}, '${year} ${pad2(date.getUTCHours())}:${pad2(
    date.getUTCMinutes()
  )}`;
};

export const formatTickMark = (time, rawScale) => {
  const date = fromChartTime(time);
  if (!date) return "";

  const scale = normalizeChartScale(rawScale);
  if (scale === "1d" || scale === "1w" || scale === "1mo") {
    if (scale === "1mo") {
      return `${MONTHS_SHORT[date.getUTCMonth()]} '${date
        .getUTCFullYear()
        .toString()
        .slice(2)}`;
    }
    return `${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`;
  }

  return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
};

export const formatCrosshairTime = (time, rawScale) => {
  const date = fromChartTime(time);
  if (!date) return "";

  const scale = normalizeChartScale(rawScale);
  if (scale === "1d" || scale === "1w" || scale === "1mo") {
    return `${WEEKDAYS_SHORT[date.getUTCDay()]} ${formatUtcDate(date, false)}`;
  }

  return `${WEEKDAYS_SHORT[date.getUTCDay()]} ${formatUtcDate(date, true)}`;
};

export const formatFundamentalDate = (raw, rawScale) => {
  const date = parseChartDate(raw);
  if (!date) return "";
  const scale = normalizeChartScale(rawScale);
  if (scale === "1d" || scale === "1w" || scale === "1mo") {
    return formatUtcDate(date, false);
  }
  return formatUtcDate(date, true);
};

export const formatPriceValue = (value, assetType = null) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";

  if (isForexAssetType(assetType)) {
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 5,
      maximumFractionDigits: 5,
    });
  }

  const abs = Math.abs(num);
  let maximumFractionDigits = 2;
  if (abs < 1) maximumFractionDigits = 4;
  if (abs < 0.01) maximumFractionDigits = 6;

  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits,
  });
};

export const formatFundamentalValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 1_000_000_000_000) {
    return `${sign}${(abs / 1_000_000_000_000).toFixed(2)}T`;
  }
  if (abs >= 1_000_000_000) {
    return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${(abs / 1_000).toFixed(1)}K`;
  }
  return `${sign}${abs.toFixed(2)}`;
};

export const logicalRangesClose = (a, b, epsilon = 0.01) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(Number(a.from) - Number(b.from)) <= epsilon &&
    Math.abs(Number(a.to) - Number(b.to)) <= epsilon
  );
};

export const getInitialVisiblePointCount = (rawScale) => {
  const scale = normalizeChartScale(rawScale);
  if (scale === "1m") return 240;
  if (scale === "2m" || scale === "3m") return 220;
  if (scale === "5m") return 200;
  if (scale === "10m" || scale === "15m") return 180;
  if (scale === "30m" || scale === "1h") return 150;
  if (scale === "2h" || scale === "3h" || scale === "4h") return 120;
  if (scale === "1d") return 180;
  if (scale === "1w") return 140;
  return 120;
};

const COMPACT_BAR_SPACING_RATIO = 0.7;

const visibleSpanWithSparseData = ({ pointCount, targetSpan }) => {
  const availableSpan = Math.max(1, Math.min(pointCount, targetSpan));
  if (pointCount >= targetSpan) return availableSpan;

  return Math.min(
    targetSpan,
    Math.ceil(availableSpan / COMPACT_BAR_SPACING_RATIO)
  );
};

export const getInitialLogicalRange = (pointCount, rawScale) => {
  if (!Number.isFinite(pointCount) || pointCount <= 0) return null;
  const targetSpan = getInitialVisiblePointCount(rawScale);
  const span = visibleSpanWithSparseData({ pointCount, targetSpan });
  const to = pointCount - 1;
  const from = to - span + 1;
  return { from, to };
};

const clampInteger = (value, min, max) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(max, Math.max(min, parsed));
};

export const normalizeDisplayRange = (rawRange) => {
  if (!rawRange || typeof rawRange !== "object") return null;

  const rawMode = String(
    rawRange.mode || rawRange.kind || rawRange.type || ""
  )
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  const modeAliases = {
    latest: "latest_bars",
    recent: "latest_bars",
    last: "latest_bars",
    last_bars: "latest_bars",
    lookback: "latest_bars",
    date_range: "time_window",
    time_range: "time_window",
    window: "time_window",
    all: "full",
    full_history: "full",
  };
  let mode = modeAliases[rawMode] || rawMode;
  if (!mode && (rawRange.bar_count || rawRange.barCount || rawRange.bars)) {
    mode = "latest_bars";
  }
  if (!mode && (rawRange.from || rawRange.to || rawRange.start || rawRange.end)) {
    mode = "time_window";
  }
  if (!["latest_bars", "time_window", "full"].includes(mode)) return null;

  const barCount = clampInteger(
    rawRange.bar_count ?? rawRange.barCount ?? rawRange.bars ?? rawRange.lookback_bars,
    5,
    500
  );
  const rightPaddingBars =
    clampInteger(
      rawRange.right_padding_bars ??
        rawRange.rightPaddingBars ??
        rawRange.padding_bars,
      0,
      80
    ) ?? (mode === "latest_bars" ? 6 : 0);
  const from = rawRange.from ?? rawRange.start ?? rawRange.start_time ?? null;
  const to = rawRange.to ?? rawRange.end ?? rawRange.end_time ?? null;

  if (mode === "latest_bars") {
    return {
      mode,
      bar_count: barCount,
      from: null,
      to: null,
      right_padding_bars: rightPaddingBars,
    };
  }

  if (mode === "time_window" && !from && !to && !barCount) return null;

  return {
    mode,
    bar_count: barCount,
    from,
    to,
    right_padding_bars: rightPaddingBars,
  };
};

const firstIndexAtOrAfter = (times, target) => {
  if (!Number.isFinite(target)) return null;
  for (let index = 0; index < times.length; index += 1) {
    if (Number(times[index]) >= target) return index;
  }
  return times.length > 0 ? times.length - 1 : null;
};

const lastIndexAtOrBefore = (times, target) => {
  if (!Number.isFinite(target)) return null;
  for (let index = times.length - 1; index >= 0; index -= 1) {
    if (Number(times[index]) <= target) return index;
  }
  return times.length > 0 ? 0 : null;
};

export const getDesiredLogicalRange = ({
  displayRange,
  pointCount,
  times = [],
  rawScale,
  timeZone = null,
}) => {
  if (!Number.isFinite(pointCount) || pointCount <= 0) return null;

  const range = normalizeDisplayRange(displayRange);
  if (!range) return null;

  const lastIndex = pointCount - 1;
  const rightPadding = range.right_padding_bars || 0;

  if (range.mode === "full") {
    return { from: 0, to: lastIndex + rightPadding };
  }

  if (range.mode === "time_window") {
    const orderedTimes =
      Array.isArray(times) && times.length === pointCount ? times : [];
    const fromTimestamp = range.from ? toChartTimestamp(range.from, timeZone) : null;
    const toTimestamp = range.to ? toChartTimestamp(range.to, timeZone) : null;
    let fromIndex =
      orderedTimes.length > 0 && Number.isFinite(fromTimestamp)
        ? firstIndexAtOrAfter(orderedTimes, fromTimestamp)
        : null;
    let toIndex =
      orderedTimes.length > 0 && Number.isFinite(toTimestamp)
        ? lastIndexAtOrBefore(orderedTimes, toTimestamp)
        : null;

    if (fromIndex == null && toIndex == null && range.bar_count) {
      const span = Math.max(1, Math.min(pointCount, range.bar_count));
      fromIndex = Math.max(0, pointCount - span);
      toIndex = lastIndex;
    } else {
      if (fromIndex == null) fromIndex = 0;
      if (toIndex == null) toIndex = lastIndex;
    }

    if (
      !Number.isFinite(fromIndex) ||
      !Number.isFinite(toIndex) ||
      toIndex < fromIndex
    ) {
      return null;
    }

    return { from: fromIndex, to: toIndex + rightPadding };
  }

  const targetSpan =
    range.bar_count || getInitialVisiblePointCount(rawScale);
  const span = visibleSpanWithSparseData({ pointCount, targetSpan });
  return {
    from: pointCount - span,
    to: lastIndex + rightPadding,
  };
};

export const addChartTimeSteps = (time, rawScale, steps) => {
  if (!Number.isFinite(time) || !Number.isFinite(steps)) return null;
  const scale = normalizeChartScale(rawScale);
  const count = Math.round(steps);
  if (count === 0) return time;

  const minuteStep = SCALE_TO_MINUTES[scale];
  if (minuteStep) {
    return time + minuteStep * 60 * count;
  }
  if (scale === "1d") {
    return time + 24 * 60 * 60 * count;
  }
  if (scale === "1w") {
    return time + 7 * 24 * 60 * 60 * count;
  }
  if (scale === "1mo") {
    const date = new Date(time * 1000);
    const next = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth() + count,
        1,
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        0
      )
    );
    return Math.floor(next.getTime() / 1000);
  }
  return time + 24 * 60 * 60 * count;
};

export const zoomLogicalRange = (range, factor) => {
  if (
    !range ||
    !Number.isFinite(range.from) ||
    !Number.isFinite(range.to) ||
    !Number.isFinite(factor) ||
    factor <= 0
  ) {
    return null;
  }

  const span = Math.max(2, range.to - range.from);
  const center = (range.from + range.to) / 2;
  const nextSpan = Math.max(2, span / factor);
  return {
    from: center - nextSpan / 2,
    to: center + nextSpan / 2,
  };
};

export const resolveChartScreenshotCanvas = (chart, container) => {
  if (chart?.takeScreenshot) {
    const canvas = chart.takeScreenshot();
    if (canvas) return canvas;
  }

  if (!container) return null;
  const canvases = Array.from(container.querySelectorAll("canvas"));
  if (canvases.length === 0) return null;

  return canvases.reduce((largest, canvas) => {
    const largestArea = (largest?.width || 0) * (largest?.height || 0);
    const currentArea = (canvas?.width || 0) * (canvas?.height || 0);
    return currentArea >= largestArea ? canvas : largest;
  }, canvases[0]);
};

export const findNearestSeriesTime = (times, rawAnchor, timeZone = null) => {
  if (!Array.isArray(times) || times.length === 0) return null;
  const anchorTime = toChartTimestamp(rawAnchor, timeZone);
  if (!Number.isFinite(anchorTime)) return times[times.length - 1] || null;

  let best = times[0];
  let bestDiff = Math.abs(times[0] - anchorTime);
  for (let idx = 1; idx < times.length; idx += 1) {
    const candidate = Number(times[idx]);
    if (!Number.isFinite(candidate)) continue;
    const diff = Math.abs(candidate - anchorTime);
    if (diff < bestDiff) {
      best = candidate;
      bestDiff = diff;
    }
  }
  return best;
};

export const createMainSeriesData = (tickerPrices = [], timeZone = null) => {
  const deduped = new Map();

  (tickerPrices || []).forEach((entry) => {
    const rawTime = entry?.datetime || entry?.date;
    const time = toChartTimestamp(rawTime, timeZone);
    const absoluteTime = toAbsoluteTimestamp(rawTime) ?? time;
    const open = toFiniteNumber(entry?.open);
    const high = toFiniteNumber(entry?.high);
    const low = toFiniteNumber(entry?.low);
    const close = toFiniteNumber(entry?.close);

    if (
      !Number.isFinite(time) ||
      open == null ||
      high == null ||
      low == null ||
      close == null
    ) {
      return;
    }

    deduped.set(time, {
      time,
      absoluteTime,
      open,
      high,
      low,
      close,
      volume: toFiniteNumber(entry?.volume) ?? 0,
    });
  });

  const candleData = Array.from(deduped.values()).sort((a, b) => a.time - b.time);
  const lineData = candleData.map((entry) => ({
    time: entry.time,
    value: entry.close,
  }));

  return {
    candleData,
    lineData,
    times: candleData.map((entry) => entry.time),
  };
};

export const alignFundamentalSeries = ({
  values = [],
  priceTimes = [],
  timeZone = null,
}) => {
  const aligned = new Map();
  const orderedPriceTimes = Array.isArray(priceTimes)
    ? [...priceTimes].filter((time) => Number.isFinite(time))
    : [];

  (values || []).forEach((entry) => {
    const y = toFiniteNumber(entry?.y);
    if (y == null) return;

    const rawTime = toChartTimestamp(entry?.x, timeZone);
    if (rawTime == null) return;

    let snapped = rawTime;
    if (orderedPriceTimes.length > 0) {
      snapped =
        orderedPriceTimes.find((time) => time >= rawTime) ||
        orderedPriceTimes[orderedPriceTimes.length - 1];
    }

    aligned.set(snapped, {
      time: snapped,
      value: y,
    });
  });

  return Array.from(aligned.values()).sort((a, b) => a.time - b.time);
};
